/**
 * BootstrapGate — resolves the platform bootstrap (`./bootstrap.ts`) before
 * any branded chrome renders (platform contract §5.1, DNKADM-0274). Wraps
 * `RouterProvider` in `main.tsx`, so the gate sits before every route —
 * including the unauthenticated deep-link → centralized-login redirect made
 * inside `AuthGate` — and before first paint of the app shell.
 *
 *  - **Pending:** a minimal, unbranded loading state.
 *  - **Failure:** a BLOCKING error screen (never a degraded shell), never a
 *    composed-host fallback (no certificate exists for one), with a retry
 *    that re-issues the request. A `404` (this host is registered to no
 *    tenant) is called out distinctly from a transient failure — both are
 *    operator-provisionable, so the copy says so plainly.
 *  - **Success:** renders `children`.
 *
 * One dev-only exception: the `/theme-preview` gallery (DNKADM-0297) bypasses
 * the gate entirely — see the note inside. Production builds contain no trace
 * of the bypass.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { BootstrapError, resolveBootstrap } from './bootstrap';

type GateState = 'pending' | 'ready' | 'failed';

const fullScreenStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: '0.75rem',
  padding: '2rem',
  textAlign: 'center',
} as const;

export function BootstrapGate({ children }: { children: ReactNode }): ReactNode {
  // Dev-only bypass for the /theme-preview gallery (DNKADM-0297 U1): local dev
  // has no API behind it, so the bootstrap fetch always fails there and this
  // gate would block the one route whose purpose is to render WITHOUT the
  // platform (no session, no bootstrap, no API). `import.meta.env.DEV` is
  // statically false in a production build, so this whole branch — condition,
  // path literal and all — is eliminated; the production gate is unchanged and
  // `scripts/assert-preview-excluded.mjs` asserts the literal never ships.
  const devPreviewBypass =
    import.meta.env.DEV &&
    (window.location.pathname === '/theme-preview' ||
      window.location.pathname === '/board-preview');

  const [state, setState] = useState<GateState>('pending');
  const [error, setError] = useState<BootstrapError | null>(null);
  const started = useRef(false);

  const attempt = useCallback(() => {
    setState('pending');
    setError(null);
    resolveBootstrap().then(
      () => setState('ready'),
      (err: unknown) => {
        setError(
          err instanceof BootstrapError
            ? err
            : new BootstrapError('Bootstrap failed unexpectedly.', null),
        );
        setState('failed');
      },
    );
  }, []);

  useEffect(() => {
    if (devPreviewBypass) return;
    // Guard against React StrictMode's dev double-invoke — one real attempt
    // per mount, mirroring AuthGate's `started` ref.
    if (started.current) return;
    started.current = true;
    attempt();
  }, [attempt, devPreviewBypass]);

  if (devPreviewBypass) return <>{children}</>;

  if (state === 'pending') {
    return (
      <div role="status" aria-live="polite" style={fullScreenStyle}>
        <p>Loading…</p>
      </div>
    );
  }

  if (state === 'failed') {
    const notRegistered = error?.status === 404;
    return (
      <div role="alert" style={fullScreenStyle}>
        <h1>
          {notRegistered ? 'This host is not set up yet' : 'Could not load platform configuration'}
        </h1>
        <p style={{ maxWidth: '32rem' }}>
          {notRegistered
            ? 'This hostname is not registered to any tenant. An operator needs to provision it on the platform before this console can be used.'
            : 'The platform bootstrap request failed. This is usually a temporary problem or something an operator needs to configure — retry, or contact an operator if it persists.'}
        </p>
        <button type="button" onClick={attempt}>
          Retry
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
